/**
 * MentionList — Gmail-fidelity contact dropdown.
 *
 * Iter 71 — complete rewrite per Principal-FE spec:
 *   • Sanitises name strings (strips outer quotes; falls back to a
 *     title-cased email-prefix when no name property is provided).
 *   • De-duplicates by email; the backend already prioritises
 *     workspace > directory > frequent > other.
 *   • Groups rows under sticky Gmail-style section headers
 *     ("Workspace Directory" + "Frequent Contacts").
 *   • Renders an avatar circle per row — profile photo when the People
 *     API returns one, otherwise a deterministic-color letter avatar.
 *   • Bold display name on line 1, muted email on line 2.
 *   • Highlights the active query substring inside the name.
 *   • Keyboard nav: ArrowUp/ArrowDown move focus; Enter/Tab inject the
 *     pill token and close the popover.
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const QUOTE_PAIRS = [['"', '"'], ["'", "'"], ["\u201c", "\u201d"], ["\u2018", "\u2019"]];

export function cleanName(raw, email) {
  let s = (raw || "").trim();
  for (const [open, close] of QUOTE_PAIRS) {
    if (s.length >= 2 && s.startsWith(open) && s.endsWith(close)) {
      s = s.slice(1, -1).trim();
    }
  }
  s = s.replace(/\s+/g, " ");
  if (s) return s;
  // Fallback: title-case the email prefix.
  const prefix = (email || "").split("@")[0] || "";
  return prefix
    .replace(/[._\-+]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// 8 muted, accessible Tailwind backgrounds picked deterministically by
// hashing the email — matches Gmail's "color-by-handle" affordance.
const AVATAR_PALETTE = [
  "bg-rose-500", "bg-amber-500", "bg-emerald-500", "bg-sky-500",
  "bg-violet-500", "bg-pink-500", "bg-indigo-500", "bg-teal-500",
];
function avatarColorFor(email) {
  const s = (email || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// Split `text` into ranges matching/not-matching `query` (case-insensitive)
// — used to bold the query letters inside the display name.
function highlightSegments(text, query) {
  if (!text || !query) return [{ text: text || "", match: false }];
  const segs = [];
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx < 0) { segs.push({ text: text.slice(i), match: false }); break; }
    if (idx > i) segs.push({ text: text.slice(i, idx), match: false });
    segs.push({ text: text.slice(idx, idx + q.length), match: true });
    i = idx + q.length;
  }
  return segs;
}

// Gmail's two section headers.
const CATEGORY_LABELS = {
  workspace: "Workspace Directory",
  directory: "Workspace Directory",
  frequent:  "Frequent Contacts",
  other:     "Frequent Contacts",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MentionList = forwardRef(function MentionList({ items, command, onPick, query }, ref) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollerRef = useRef(null);

  // Normalise + group. The backend already sorts by priority; we just
  // bucket into the two Gmail sections preserving order, then flatten so
  // `selectedIndex` can index into a single array.
  const { flat, sections } = useMemo(() => {
    const cleaned = (items || []).map((it) => ({
      ...it,
      name: cleanName(it.name, it.email),
    }));
    // De-dup by email (case-insensitive) — defensive: backend already does
    // this, but pasted/cached results may still collide.
    const seenEmails = new Set();
    const deduped = [];
    for (const it of cleaned) {
      const k = (it.email || "").toLowerCase();
      if (!k || seenEmails.has(k)) continue;
      seenEmails.add(k);
      deduped.push(it);
    }
    // Bucket by section label.
    const buckets = new Map();
    for (const it of deduped) {
      const label = CATEGORY_LABELS[it.category] || "Frequent Contacts";
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label).push(it);
    }
    // Preserve canonical section order regardless of insertion order.
    const ordered = ["Workspace Directory", "Frequent Contacts"]
      .filter((l) => buckets.has(l))
      .map((label) => ({ label, items: buckets.get(label) }));
    const flatList = ordered.flatMap((s) => s.items);
    return { flat: flatList, sections: ordered };
  }, [items]);

  // Reset focus whenever the result set changes.
  useEffect(() => { setSelectedIndex(0); }, [items]);

  const select = (idx) => {
    const item = flat[idx];
    if (!item) return;
    onPick?.(item);
    // The TipTap command uses `label` as the rendered pill text — pass
    // the cleaned name so quoted / messy strings can never leak into the
    // editor.
    command({ id: item.email, label: item.name || item.email });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") {
        setSelectedIndex((i) => (i + 1) % Math.max(flat.length, 1));
        return true;
      }
      if (event.key === "ArrowUp") {
        setSelectedIndex((i) => (i + flat.length - 1) % Math.max(flat.length, 1));
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        select(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  // Keep the focused row visible when arrow-keying through long lists.
  useEffect(() => {
    if (!scrollerRef.current) return;
    const node = scrollerRef.current.querySelector(`[data-mention-idx="${selectedIndex}"]`);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  if (!flat.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs text-slate-400 italic" data-testid="mention-list-empty">
        No matches — keep typing
      </div>
    );
  }

  // Render. We use a running counter so `flat`-indices stay aligned with
  // the selectedIndex state across grouped headers.
  let runningIdx = 0;
  return (
    <div
      ref={scrollerRef}
      className="bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden w-[340px] max-h-80 overflow-y-auto"
      data-testid="mention-list"
    >
      {sections.map((section) => (
        <div key={section.label}>
          <div
            className="sticky top-0 z-10 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-50/95 backdrop-blur border-b border-slate-100"
            data-testid={`mention-section-${section.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {section.label}
          </div>
          {section.items.map((item) => {
            const idx = runningIdx++;
            const active = idx === selectedIndex;
            const nameSegs = highlightSegments(item.name || item.email, query || "");
            const initial = ((item.name || item.email)[0] || "?").toUpperCase();
            return (
              <button
                key={`${section.label}-${item.email}`}
                type="button"
                data-mention-idx={idx}
                onClick={() => select(idx)}
                onMouseEnter={() => setSelectedIndex(idx)}
                data-testid={`mention-item-${idx}`}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
              >
                {/* Avatar — Google profile photo if available, otherwise a
                    deterministic-color initial tile. */}
                {item.photo_url ? (
                  <img
                    src={item.photo_url}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover shrink-0 ring-1 ring-slate-200"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span
                    className={`h-8 w-8 rounded-full ${avatarColorFor(item.email)} text-white grid place-items-center text-xs font-semibold shrink-0`}
                  >
                    {initial}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">
                    {nameSegs.map((s, i) => (
                      <span key={i} className={s.match ? "underline decoration-blue-400 decoration-2 underline-offset-2" : ""}>{s.text}</span>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate" data-testid={`mention-email-${idx}`}>{item.email}</div>
                </div>
                {item.hint && (
                  <div className="text-[10px] text-slate-400 shrink-0 ml-2">{item.hint}</div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
});

export default MentionList;
